import React, { useCallback, useMemo, useState } from 'react';
import Input from '../Input';
import { View } from 'react-native';
import Checkbox from '../Checkbox';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import InputFeedback from '../InputFeedback';
import Select from '../Select';
import { getAddressByCepUseCase } from '~/useCases/AssegurouAutoUseCases/GetAddressByCepUseCase';
import { isWeb } from '~/utils/device';
import { useForm } from 'react-hook-form';
import { states } from '~/utils/string';
import Button from '../Button';
import Text from '../Text';
import Banner from '../Banner';
import Footer from '../Footer';
import KeyboardScreenContainer from '../KeyboardScreenContainer';
import DontKnowCep from '../DontKnowCep';
import type { InsuranceType } from '~/@types/insurance';

interface AddressFormData {
  zipcode: string;
  street: string;
  noNumber: boolean;
  number: string;
  noComplement: boolean;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
}

export const schema = yup
  .object()
  .shape({
    zipcode: yup.string().required('Este campo é obrigatório'),
    street: yup.string().required('Este campo é obrigatório'),
    noNumber: yup.boolean(),
    noComplement: yup.boolean(),
    number: yup.string().when('noNumber', {
      is: false,
      then: yup
        .string()
        .required(
          `Informe o número da residência ou toque no checkbox "Sem número".`
        ),
    }),
    complement: yup.string().when('noComplement', {
      is: false,
      then: yup
        .string()
        .required(
          `Informe o complemento ou toque no checkbox "Sem complemento".`
        ),
    }),
    neighborhood: yup.string().required('Este campo é obrigatório'),
    city: yup.string().required('Este campo é obrigatório'),
    state: yup.string().required('.'),
  })
  .required();

interface Props {
  insuranceType: InsuranceType;
  onValidate: (data: AddressFormData) => void;
}

const AddressInfoInput: React.FC<Props> = ({ insuranceType, onValidate }) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    setError,
    clearErrors,
    watch,
  } = useForm<AddressFormData>({
    resolver: yupResolver(schema),
    defaultValues: { noNumber: false, noComplement: false },
  });

  const [loadingZipcode, setLoadingZipcode] = useState(false);
  const [loadedZipcode, setLoadedZipcode] = useState(false);

  const isAssurantService = useMemo(
    () =>
      insuranceType === 'bag-protection' ||
      insuranceType === 'income-protection',
    [insuranceType]
  );

  const hasNoNumber = watch('noNumber');
  const hasNoComplement = watch('noComplement');

  const loadAddress = useCallback(
    async (zipcode: string) => {
      clearErrors();

      const addressResponse = await getAddressByCepUseCase(zipcode);

      if (addressResponse.success) {
        const { street, neighborhood, city, state } = addressResponse.address;
        setLoadedZipcode(true);
        setValue('street', street.toUpperCase());
        setValue('neighborhood', neighborhood.toUpperCase());
        setValue('city', city.toUpperCase());
        setValue('state', state.toUpperCase());
        setLoadingZipcode(false);
        setError('zipcode', {});
      } else {
        setError('zipcode', { message: 'CEP não encontrado' });
        setLoadedZipcode(false);
        setLoadingZipcode(false);
      }
    },
    [clearErrors, setError, setValue]
  );

  const onSubmit = useCallback(
    (data: AddressFormData) => {
      setLoadingZipcode(true);
      setLoadingZipcode(false);
      onValidate(data);
    },
    [onValidate]
  );

  return (
    <KeyboardScreenContainer
      footer={
        <View className="flex xl:hidden">
          <Footer onSubmit={handleSubmit(onSubmit)} disabled={loadingZipcode} />
        </View>
      }
    >
      <View className="flex-1 container mx-auto xl:flex-row xl:pt-12 xl:pb-24">
        <Banner type={insuranceType} />

        <View className="mt-8 xl:mt-0 px-6 flex-1 xl:ml-24">
          <Text className="text-xl mb-4 text-gray-600">
            Qual é o CEP do endereço residencial do segurado?
          </Text>

          <Text className="text-gray-400 text-sm mb-8">
            O endereço nos ajuda a manter suas informações seguras e atualizadas
          </Text>

          <Input
            control={control}
            label="Qual é o CEP?"
            name="zipcode"
            placeholder="Insira seu CEP sem hífen"
            error={errors.zipcode?.message}
            maskConfig={{ type: 'zip-code' }}
            keyboardType="number-pad"
            className="mb-[-20px]"
            loading={loadingZipcode}
            onChange={async (e) => {
              if (e.nativeEvent.text.length === 9) {
                setLoadingZipcode(true);
                await loadAddress(e.nativeEvent.text);
              } else {
                setLoadingZipcode(false);
                setLoadedZipcode(false);
              }
            }}
            maxLength={9}
          />

          <DontKnowCep errorMessage={errors.zipcode?.message} />

          <View className={`${loadedZipcode ? 'opacity-1' : 'hidden'} `}>
            <Input
              control={control}
              label="Logradouro"
              name="street"
              keyboardType="ascii-capable"
              error={errors.street?.message}
              maxLength={isAssurantService ? 30 : 150}
            />

            <View className="flex-row items-center">
              <View className="flex-[0.5] mb-6">
                <Input
                  control={control}
                  label="Número"
                  name="number"
                  editable={!hasNoNumber}
                  maskConfig={{ type: 'only-numbers' }}
                  keyboardType="number-pad"
                  placeholder="Ex.: 123"
                  className={`${isWeb ? '' : 'flex-[0.5]'}`}
                  maxLength={10}
                />

                <Checkbox
                  control={control}
                  name="noNumber"
                  className={'mt-[-20px]'}
                  error={errors.number?.message}
                >
                  Sem número
                </Checkbox>
              </View>

              <View className="flex-[0.5] mb-6 ml-2">
                <Input
                  control={control}
                  label="Complemento"
                  name="complement"
                  editable={!hasNoComplement}
                  keyboardType="ascii-capable"
                  placeholder="Ex.: Casa 2"
                  maxLength={10}
                />
                <Checkbox
                  control={control}
                  name="noComplement"
                  className={'mt-[-20px]'}
                  error={errors.complement?.message}
                >
                  Sem complemento
                </Checkbox>
              </View>
            </View>

            <InputFeedback
              message={errors.number?.message}
              className="mt-[-12px]"
            />

            <InputFeedback
              message={errors.complement?.message}
              className={`${errors.number?.message ? '' : 'mt-[-12px]'}`}
            />

            <Input
              control={control}
              label="Bairro"
              name="neighborhood"
              keyboardType="ascii-capable"
              error={errors.neighborhood?.message}
              maxLength={isAssurantService ? 30 : 150}
            />

            <View className="flex-row items-center mb-4">
              <Input
                control={control}
                label="Cidade"
                name="city"
                error={errors.city?.message}
                keyboardType="ascii-capable"
                className="mr-2 flex-1"
                maxLength={isAssurantService ? 30 : 150}
              />
              <Select
                control={control}
                label="Estado"
                name="state"
                placeholder="Selecione"
                error={errors.state?.message}
                items={states}
                containerStyle={{ width: 80 }}
              />
            </View>
          </View>

          {isWeb && (
            <View className="flex-1 justify-end hidden xl:flex z-0">
              <Button className="w-full" onPress={handleSubmit(onSubmit)}>
                CONTINUAR
              </Button>
            </View>
          )}
        </View>
      </View>
    </KeyboardScreenContainer>
  );
};

export default AddressInfoInput;
